import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { subscribeRealtime } from '../services/realtime';
import { accessApi } from '../api/moderation';
import { useAuth } from './AuthContext';
import { usePublishingIdentity } from './PublishingIdentityContext';

const AccessContext = createContext(null);

export const useAccess = () => {
  const value = useContext(AccessContext);
  if (!value) throw new Error('useAccess must be used within AccessProvider');
  return value;
};

export function AccessProvider({ children }) {
  const { isAuthenticated, currentUserId, currentUser, loading: authLoading } = useAuth();
  const rolesKey = JSON.stringify(currentUser?.roles || []);
  const rolesRef = useRef([]);
  const version = useRef(0);
  const { activeType } = usePublishingIdentity();
  const [permissions, setPermissions] = useState([]);
  const [owner, setOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const requestVersion = ++version.current;
    if (!isAuthenticated || !rolesRef.current.length) {
      setPermissions([]);
      setOwner(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await accessApi.myPermissions();
      if (version.current !== requestVersion) return;
      setPermissions(response?.permissions || []);
      setOwner(Boolean(response?.owner));
    } catch (_) {
      if (version.current !== requestVersion) return;
      setPermissions([]);
      setOwner(false);
    } finally {
      if (version.current === requestVersion) setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    rolesRef.current = JSON.parse(rolesKey);
    setPermissions([]);
    setOwner(false);
    if (!authLoading) refresh();
    return () => { version.current++; };
  }, [authLoading, currentUserId, rolesKey, refresh]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return undefined;
    const unsubscribe = subscribeRealtime(event => {
      if (event.type === 'access.changed') {
        version.current++;
        rolesRef.current = event.data?.roles || [];
        setPermissions(event.data?.permissions || []);
        setOwner(Boolean(event.data?.owner));
        setLoading(false);
      }
    });
    // The server sends access.changed snapshots on authentication and event-stream
    // recovery, as well as role edits. Reconnects need no additional HTTP reads.
    return unsubscribe;
  }, [authLoading, currentUserId, isAuthenticated, refresh]);

  const value = useMemo(() => {
    const permissionSet = new Set(permissions);
    const actingAsTeam = activeType === 'team';
    return {
      permissions: actingAsTeam ? [] : permissions,
      owner: actingAsTeam ? false : owner,
      accountOwner: owner,
      accountCanManageMaintenance: owner || permissionSet.has('MAINTENANCE_MANAGE'),
      loading,
      hasPermission: (key) => !actingAsTeam && (owner || permissionSet.has(key)),
      hasAnyPermission: (...keys) => !actingAsTeam && (owner || keys.some((key) => permissionSet.has(key))),
      refresh,
    };
  }, [activeType, permissions, owner, loading, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export default AccessContext;
